import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { AppNavbar } from '../components/AppNavbar';
import { 
  FileText, Send, Clock, Edit, Trash2, Plus, Calendar,
  Image, Video, Mail, Eye, MoreHorizontal, Copy
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MobileTabBar } from '../components/MobileTabBar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

export const CampaignsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    filterCampaigns();
  }, [selectedStatus, campaigns]);

  const fetchCampaigns = async () => {
    try {
      const response = await apiClient.get('/campaigns');
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const filterCampaigns = () => {
    if (selectedStatus === 'all') {
      setFilteredCampaigns(campaigns);
    } else {
      setFilteredCampaigns(campaigns.filter(c => c.status === selectedStatus));
    }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await apiClient.delete(`/campaigns/${campaignId}`);
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const handleClone = async (campaign) => {
    try {
      const cloneData = {
        type: campaign.type || 'post',
        caption: campaign.caption ? `${campaign.caption} (Copy)` : '',
        template_id: campaign.template_id,
        content_data: campaign.content_data,
      };
      await apiClient.post('/campaigns', cloneData);
      toast.success('Campaign cloned as draft');
      fetchCampaigns();
    } catch {
      toast.error('Failed to clone campaign');
    }
  };

  const handleEdit = (campaign) => {
    const propertyData = campaign.property_data || {};
    const isPrint = ['flyer', 'postcard', 'brochure'].includes(campaign.type);

    // Reel with composition
    if (campaign.type === 'reel' && campaign.content_data?.composition_id) {
      navigate(`/reel-editor?comp=${campaign.content_data.composition_id}`);
      return;
    }

    // Reel without composition — open reel editor with template
    if (campaign.type === 'reel') {
      const reelTemplate = campaign.content_data?.template || {};
      navigate(`/reel-editor?template=${reelTemplate.template_id || reelTemplate.reel_template_id || ''}`, {
        state: { propertyData, reelTemplate, fromMagicCreate: true }
      });
      return;
    }

    // Carousel
    if (campaign.type === 'carousel') {
      navigate(`/carousel-editor?property=${encodeURIComponent(JSON.stringify(propertyData))}`, {
        state: { propertyData, fromMagicCreate: true }
      });
      return;
    }

    // Newsletter — open the newsletter builder for the existing draft when we
    // have a template_id; only fall back to the email-lists landing if the
    // campaign somehow has no template linked.
    if (campaign.type === 'newsletter') {
      if (campaign.template_id) {
        navigate(`/create-campaign/${campaign.template_id}?campaign=${campaign.campaign_id}`, {
          state: { propertyData, fromMagicCreate: true }
        });
      } else {
        navigate('/email-lists', { state: { propertyData, fromMagicCreate: true } });
      }
      return;
    }

    // Standard template-based (post, story, flyer, postcard, brochure)
    if (campaign.template_id) {
      // Pass `campaign` query param so the editor updates this draft instead
      // of creating a duplicate on save/schedule/publish.
      navigate(`/create-campaign/${campaign.template_id}?campaign=${campaign.campaign_id}`, {
        state: { propertyData, isPrint, fromMagicCreate: true }
      });
    } else {
      toast.info('No template linked to this campaign');
    }
  };

  const handlePublish = async (campaignId) => {
    try {
      await apiClient.post(`/campaigns/${campaignId}/publish`);
      toast.success('Campaign published!');
      fetchCampaigns();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to publish campaign';
      toast.error(errorMessage);
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'post': return <Image className="h-4 w-4" />;
      case 'reel': return <Video className="h-4 w-4" />;
      case 'newsletter': return <Mail className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-700', label: 'Draft' },
      scheduled: { color: 'bg-blue-100 text-blue-700', label: 'Scheduled' },
      published: { color: 'bg-green-100 text-green-700', label: 'Published' },
      sent: { color: 'bg-purple-100 text-purple-700', label: 'Sent' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-0">
      <div className="noise-texture" />
      <AppNavbar />

      {/* Content */}
      <main className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="campaigns-title">My Campaigns</h2>
          <Button onClick={() => navigate('/templates')} data-testid="create-campaign-button">
            <Plus className="h-4 w-4 mr-2" />
            Create Campaign
          </Button>
        </div>

        {/* Filter Tabs */}
        <div className="mb-8">
          <Tabs value={selectedStatus} onValueChange={setSelectedStatus}>
            {/* Horizontal scroll wrapper so the 4 status filter chips never
                overflow off-screen on narrow viewports. */}
            <div className="-mx-4 sm:mx-0 overflow-x-auto no-scrollbar">
              <TabsList className="w-max">
                <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
                <TabsTrigger value="draft" data-testid="filter-draft">Drafts</TabsTrigger>
                <TabsTrigger value="scheduled" data-testid="filter-scheduled">Scheduled</TabsTrigger>
                <TabsTrigger value="published" data-testid="filter-published">Published</TabsTrigger>
              </TabsList>
            </div>
          </Tabs>
        </div>

        {/* Campaigns List */}
        {loading ? (
          <div className="text-center py-20">
            <p>Loading campaigns...</p>
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary/20 flex items-center justify-center">
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-2">No campaigns yet</h3>
            <p className="text-muted-foreground mb-4">Create your first campaign to get started</p>
            <Button onClick={() => navigate('/templates')} data-testid="empty-create-campaign">
              <Plus className="h-4 w-4 mr-2" />
              Create Campaign
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="campaigns-list">
            {filteredCampaigns.map((campaign) => (
              <div
                key={campaign.campaign_id}
                className="border border-border/50 bg-card rounded-lg p-4 hover:border-primary/30 transition-colors"
                data-testid={`campaign-card-${campaign.campaign_id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="flex items-start gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-lg bg-secondary/20 flex items-center justify-center shrink-0">
                      {getTypeIcon(campaign.type)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium break-words">
                          {campaign.property_data?.address
                            ? campaign.property_data.address
                            : (campaign.name || `${(campaign.type || 'post').charAt(0).toUpperCase() + (campaign.type || 'post').slice(1)} Campaign`)}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          {campaign.type || 'post'}
                        </span>
                        {getStatusBadge(campaign.status)}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2 max-w-lg">
                        {campaign.caption || 'No caption added'}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Created: {formatDate(campaign.created_at)}
                        </span>
                        {campaign.scheduled_for && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Scheduled: {formatDate(campaign.scheduled_for)}
                          </span>
                        )}
                        {campaign.published_at && (
                          <span className="flex items-center gap-1">
                            <Send className="h-3 w-3" />
                            Published: {formatDate(campaign.published_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action row: edit / publish / 3-dot — full width on mobile,
                      inline on md+ so it sits to the right of the meta. */}
                  <div className="flex items-center gap-2 flex-wrap md:flex-nowrap md:shrink-0 w-full md:w-auto md:justify-end">
                    {campaign.status === 'draft' && (
                      campaign.type === 'reel' ? (
                        <Button
                          size="sm"
                          onClick={() => handleEdit(campaign)}
                          data-testid={`open-reel-maker-${campaign.campaign_id}`}
                        >
                          <Video className="h-4 w-4 mr-1" />
                          Open in Reel Maker
                        </Button>
                      ) : (
                        <Button 
                          size="sm" 
                          onClick={() => handlePublish(campaign.campaign_id)}
                          data-testid={`publish-campaign-${campaign.campaign_id}`}
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Publish
                        </Button>
                      )
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`campaign-menu-${campaign.campaign_id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={() => handleEdit(campaign)}
                          data-testid={`edit-campaign-${campaign.campaign_id}`}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleClone(campaign)}
                          data-testid={`clone-campaign-${campaign.campaign_id}`}
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Clone
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Eye className="h-4 w-4 mr-2" />
                          Preview
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-destructive"
                          onClick={() => handleDelete(campaign.campaign_id)}
                          data-testid={`delete-campaign-${campaign.campaign_id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <MobileTabBar />
    </div>
  );
};
